import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Avatar,
    TextField
} from '@mui/material';
import { Plus, Trash2, SquarePen, Upload, X } from 'lucide-react';
import { getAllFoundersData, createFounderData, updateFounderData, deleteFounderData } from '../../Services/ContentHub/FoundersApi';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const Founders = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [founders, setFounders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentFounder, setCurrentFounder] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '' });
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [modalError, setModalError] = useState(null);

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedFounderId, setSelectedFounderId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const fetchFounders = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllFoundersData();
            if (response && response.success) {
                setFounders(response.data);
            } else {
                setError("Failed to load founders.");
            }
        } catch (err) {
            setError("Failed to fetch founders. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFounders();
    }, [fetchFounders]);

    const handleOpenModal = (founder = null) => {
        setModalError(null);
        setFieldErrors({});
        if (founder) {
            setIsEdit(true);
            setCurrentFounder(founder);
            setFormData({
                name: founder.name || '',
                description: founder.description || ''
            });
            setImagePreview(founder.image);
        } else {
            setIsEdit(false);
            setCurrentFounder(null);
            setFormData({ name: '', description: '' });
            setImagePreview(null);
        }
        setSelectedImage(null);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentFounder(null);
        setFormData({ name: '', description: '' });
        setSelectedImage(null);
        setImagePreview(null);
        setModalError(null);
        setFieldErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        if (name === 'name') {
            // Only allow letters and spaces
            const sanitizedValue = value.replace(/[^a-zA-Z\s]/g, "");
            setFormData(prev => ({
                ...prev,
                name: sanitizedValue
            }));
            if (fieldErrors.name) {
                setFieldErrors(prev => ({ ...prev, name: null }));
            }
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (!file.type.startsWith("image/")) {
                setFieldErrors(prev => ({ ...prev, foundersImage: "Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed" }));
                setModalError("File type not supported! Expected an image.");
                showSnackbar("Only image files are allowed", "error");
                e.target.value = "";
                return;
            }

            setSelectedImage(file);
            if (fieldErrors.foundersImage) {
                setFieldErrors(prev => ({ ...prev, foundersImage: null }));
            }
            if (modalError) {
                setModalError(null);
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fieldErrors.foundersImage) {
            setFieldErrors(prev => ({
                ...prev,
                foundersImage: null
            }));
        }
    };

    const handleSubmit = async () => {
        const clientErrors = {};
        if (!formData.name || !formData.name.trim()) {
            clientErrors.name = "Name is required";
        } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
            clientErrors.name = "Name should contain only letters and spaces";
        }

        if (!formData.description) clientErrors.description = "Description is required";

        if (selectedImage && !selectedImage.type.startsWith("image/")) {
            clientErrors.foundersImage = "Only image files are allowed";
        }

        if (Object.keys(clientErrors).length > 0) {
            setFieldErrors(clientErrors);
            setModalError("Please fill in all required fields correctly");
            return;
        }

        setSubmitting(true);
        setModalError(null);
        setFieldErrors({});

        const formPayload = new FormData();
        formPayload.append('name', formData.name);
        formPayload.append('description', formData.description);



        if (selectedImage) {
            formPayload.append('foundersImage', selectedImage);
        }

        try {
            if (isEdit) {
                await updateFounderData(currentFounder.id, formPayload);
                showSnackbar("Founder updated successfully!", "success");
            } else {
                await createFounderData(formPayload);
                showSnackbar("Founder created successfully!", "success");
            }

            handleCloseModal();
            fetchFounders();
        } catch (err) {
            console.error("Save Founder Error:", err);
            if (err.response && err.response.data) {
                const resData = err.response.data;
                if (resData.details || resData.errors) {
                    const errors = {};
                    if (resData.details) {
                        resData.details.forEach(detail => {
                            errors[detail.field] = detail.message;
                        });
                    }
                    setFieldErrors(errors);

                    let errorMsg = resData.message || "Validation failed";
                    if (resData.errors && resData.errors.length > 0) {
                        errorMsg = resData.errors.join(", ");
                    }
                    setModalError(errorMsg);
                } else {
                    setModalError(resData.message || `Failed to ${isEdit ? 'update' : 'create'} founder`);
                }
            } else {
                setModalError(err.message || `Failed to ${isEdit ? 'update' : 'create'} founder`);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedFounderId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedFounderId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteFounderData(selectedFounderId);
                if (response && response.success) {
                    setDeleteModalOpen(false);
                    setSelectedFounderId(null);
                    showSnackbar("Founder deleted successfully!", "success");
                    fetchFounders();
                } else {
                    setDeleteError(response?.message || "Failed to delete founder");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete founder";
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Founders
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button
                            disableElevation
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                            variant="contained"
                            startIcon={<Plus size={18} />}
                            onClick={() => handleOpenModal()}
                            sx={{
                                bgcolor: '#FFBD65',

                                px: 2.5,
                                color: '#fff',
                                borderRadius: '6px',
                                textTransform: 'none',
                                height: '40px',
                                fontSize: '0.85rem'
                            }}
                        >
                            Add Founder
                        </Button>
                    </PermissionWrapper>
                </Box>

                {!openModal && error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1000 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '80px' }}>Image</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '180px' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : founders.length > 0 ? (
                                    founders.map((founder, idx) => (
                                        <TableRow key={founder.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Avatar
                                                    src={founder.image}
                                                    variant="rounded"
                                                    sx={{ width: 32, height: 32, borderRadius: '4px' }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 500, color: '#222', fontSize: '0.8rem' }}>{founder.name}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, color: '#555', maxWidth: 300, fontSize: '0.8rem' }}>
                                                <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.8rem' }}>
                                                    {founder.description}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {founder.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {founder.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(founder.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(founder.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small"
                                                                sx={{ color: '#FFBD65' }}
                                                                onClick={() => handleOpenModal(founder)}
                                                            >
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small"
                                                                sx={{ color: '#FF4D49' }}
                                                                onClick={() => handleDeleteClick(founder.id)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No founders found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Add/Edit Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ color: '#222', borderBottom: '1px solid #F0F0F0', py: 2, fontWeight: 600, fontSize: '1.1rem' }}>
                        {isEdit ? 'Edit Founder' : 'Add Founder'}
                    </DialogTitle>
                    <DialogContent sx={{ py: 3 }}>
                        {modalError && <Alert severity="error" sx={{ mb: 2, borderRadius: '6px', fontSize: '0.8rem' }} onClose={() => setModalError(null)}>{modalError}</Alert>}
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter founder name"
                                error={Boolean(fieldErrors.name)}
                                helperText={fieldErrors.name}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                multiline
                                rows={3}
                                placeholder="Enter description"
                                error={Boolean(fieldErrors.description)}
                                helperText={fieldErrors.description}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>Founder Image</Typography>
                                <Box
                                    sx={{
                                        border: fieldErrors.foundersImage ? '2px dashed #FF4D49' : '2px dashed #E0E0E0',
                                        borderRadius: '6px',
                                        p: 2,
                                        textAlign: 'center',
                                        bgcolor: '#FAFAFA',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        '&:hover': { borderColor: fieldErrors.foundersImage ? '#FF4D49' : '#FFBD65' }
                                    }}
                                >
                                    {imagePreview ? (
                                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                            <img src={imagePreview} alt="Preview"
                                                style={{ maxHeight: 120, maxWidth: '100%', borderRadius: '4px', display: 'block' }}
                                            />
                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                size="small"
                                                onClick={handleRemoveImage}
                                                sx={{
                                                    position: 'absolute',
                                                    top: -10,
                                                    right: -10,
                                                    bgcolor: '#FFFFFF',
                                                    color: '#EF4444',
                                                    boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
                                                    '&:hover': {
                                                        bgcolor: '#FEE2E2',
                                                        color: '#DC2626'
                                                    }
                                                }}
                                            >
                                                <X size={14} color="#EF4444" />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <label style={{ width: '100%', height: '100%', display: 'block', cursor: 'pointer' }}>
                                            <input type="file" accept="image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml" hidden onChange={handleImageChange} />
                                            <Stack spacing={1} alignItems="center">
                                                <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: '#FFF8ED', color: '#FFBD65', display: 'inline-flex' }}>
                                                    <Upload size={20} />
                                                </Box>
                                                <Typography sx={{ fontSize: '0.85rem', color: '#444' }}>Click to upload image</Typography>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>PNG or JPG (max. 5MB)</Typography>
                                            </Stack>
                                        </label>
                                    )}
                                </Box>
                                {fieldErrors.foundersImage && (
                                    <Typography variant="caption" sx={{ color: '#FF4D49', mt: 0.5, display: 'block', fontSize: '0.75rem' }}>
                                        {fieldErrors.foundersImage}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, borderTop: '1px solid #F0F0F0' }}>
                        <Button
                            disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleCloseModal}
                            sx={{ borderRadius: '6px', textTransform: 'none', color: '#222', px: 2.5, fontSize: '0.85rem' }}
                        >
                            Cancel
                        </Button>
                        <Button disableFocusRipple disableRipple disableTouchRipple disableElevation
                            onClick={handleSubmit} disabled={submitting} variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                px: 3,
                                color: '#fff',
                                borderRadius: '6px',
                                textTransform: 'none',
                                fontSize: '0.85rem',
                                '&:disabled': { bgcolor: '#F0F0F0' }
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update Founder' : 'Add Founder')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Modal */}
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Founder"
                    description="Are you sure you want to delete this founder? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default Founders;
